import React, { useState, useEffect,  useRef, useCallback } from 'react';
import { Gift, Search, User, Sparkles, ExternalLink, ShoppingCart, Loader, Plus, Settings, CreditCard } from 'lucide-react';
import analytics from '../../utils/analytics';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';


export function CustomSlider({ value, min, max, onChange, marks = [], className = "" }) {
  const trackRef = useRef(null);
  const [internalValue, setInternalValue] = useState(value);
  const [dragging, setDragging] = useState(false);
  

  // Sync with parent when not dragging
  useEffect(() => {
    if (!dragging) setInternalValue(value);
  }, [value, dragging]);

  // Map internal numeric to display string
  const displayValue = React.useMemo(() => {
    if (Array.isArray(marks) && marks.length > 0) {
      const idx = Math.round(internalValue - min);
      return marks[idx] || "";
    }
    return internalValue;
  }, [internalValue, marks, min]);

  // Calculate new value from clientX
  const calcValue = (clientX) => {
    const track = trackRef.current;
    if (!track) return internalValue;
    const { left, width } = track.getBoundingClientRect();
    const pctRaw = (clientX - left) / width;
    const pct = Math.max(0, Math.min(1, pctRaw));
    return Math.round(min + pct * (max - min));
  };

  // Start dragging
  const handlePointerDown = (e) => {
    e.preventDefault();
    const startVal = calcValue(e.clientX);
    setInternalValue(startVal);
    setDragging(true);
    
    const onMove = (mv) => {
      const moved = calcValue(mv.clientX);
      setInternalValue(moved);
    };
    const onUp = (up) => {
      const finalVal = calcValue(up.clientX);
      setInternalValue(finalVal);
      setDragging(false);
      onChange({ target: { value: finalVal } });
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  // Compute thumb percent
  const pct = ((internalValue - min) / (max - min)) * 100;

  return (
    <div
      ref={trackRef}
      onPointerDown={handlePointerDown}
      className={`relative h-6 bg-secondary-200 rounded-lg ${className}`}
      style={{ touchAction: 'none', userSelect: 'none' }}
    >
      {/* Display value above thumb */}
      <div
        className="absolute -top-6 text-sm font-medium text-primary-600 pointer-events-none whitespace-nowrap"
        style={{ left: `${pct}%`, transform: 'translateX(-50%)' }}
      >
        {displayValue}
      </div>

      {/* Filled track */}
      <div
        className="absolute top-1/2 transform -translate-y-1/2 h-2 bg-primary-500 rounded-full"
        style={{ width: `${pct}%` }}
      />

      {/* Thumb */}
      <div
        className="absolute bg-white border-2 border-primary-600 rounded-full touch-none"
        style={{ left: `${pct}%`, top: '50%', transform: 'translate(-50%, -50%)', width: '2rem', height: '2rem' }}
      />
    </div>
  );
}



const GiftFinderApp = ({ setCurrentPage }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [categoriesError, setCategoriesError] = useState('');
  const [retryCount, setRetryCount] = useState(0);
  const [subcategories, setSubcategories] = useState({});
  const [giftResults, setGiftResults] = useState(null);
  const [error, setError] = useState('');
  const [similarProducts, setSimilarProducts] = useState({});
  const [expandedProducts, setExpandedProducts] = useState({});
  const [expandedCategories, setExpandedCategories] = useState({});
  const [subcategoriesLoading, setSubcategoriesLoading] = useState({});
  const [subcategoriesError, setSubcategoriesError] = useState({});
  const [subcategoriesRetryCount, setSubcategoriesRetryCount] = useState({});
  const [compactFormExpanded, setCompactFormExpanded] = useState(false);
  const [loadingSimilar, setLoadingSimilar] = useState({});

  
  
  
  const [formData, setFormData] = useState({
    age: 25,
    gender: '',
    budget: '200-500',
    interests: [],
    interestDetails: {},
    otherInterests: '',
    relationship: '',
    occasion: '',
    additional: '',
    specificRequest: ''
  });

  const budgetOptions = [
    { value: 'under-200', label: 'Under 200 kr' },
    { value: '200-500', label: '200-500 kr' },
    { value: '500-1000', label: '500-1000 kr' },
    { value: '1000-2000', label: '1000-2000 kr' },
    { value: 'over-2000', label: 'Over 2000 kr' }
  ];

  const budgetIndex = budgetOptions.findIndex(b => b.value === formData.budget);

  const budgetMarks = budgetOptions.map(option => option.label);

  const options = {
    relationships: ['Familie', 'Venn', 'Partner', 'Kollega', 'Barn', 'Foreldre', 'Besteforeldre', 'Andre'],
    occasions: ['Bursdag', 'Jul', 'Valentinsdag', 'Jubileum', 'Takk', 'Beklager', 'Bare fordi', 'Andre']
  };

  // Ved oppstart av gavefinneren
  useEffect(() => {
    analytics.giftFinder.started();
  }, []);

   // Scroll til toppen når step endres
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [step]);

  useEffect(() => {
  // When compact form is opened, reset all expanded categories
  if (compactFormExpanded && step === 4) {
    setExpandedCategories({});
  }
}, [compactFormExpanded, step]);



  // Ved klikk på eksterne lenker
const handleExternalClick = (productName, linkType, url) => {
  analytics.giftFinder.productClicked(productName, linkType);
  window.open(url, '_blank', 'noopener,noreferrer');
};


  // API calls
 const fetchCategories = useCallback(async (isRetry = false) => {
  try {
    setCategoriesLoading(true);
    if (!isRetry) {
      setCategoriesError('');
    }
    
    // Lag en manual timeout controller
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 sekunder
    
    const response = await fetch(`${API_BASE_URL}/getCategories?ageGroup=${formData.age}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      signal: controller.signal
    });
    
    clearTimeout(timeoutId); // Avbryt timeout hvis requesten fullføres
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    
    if (data.success && data.categories) {
         
     setCategories(data.categories);

      // Remove any selected interests no longer in the new categories list
      setFormData(prev => {
        const validIds = data.categories.map(cat => cat.id);
        const filteredInterests = prev.interests.filter(id => validIds.includes(id));
        const filteredDetails = Object.fromEntries(
          Object.entries(prev.interestDetails)
            .filter(([catId]) => filteredInterests.includes(catId))
        );
        return {
          ...prev,
          interests: filteredInterests,
          interestDetails: filteredDetails
        };
      });
      setCategoriesError('');
      setRetryCount(0);
    } else {
      throw new Error(data.message || 'Ingen kategorier mottatt fra server');
    }
  } catch (err) {
    console.error('Feil ved lasting av kategorier:', err);
    setCategoriesError(err.name === 'AbortError' ? 'Forespørsel tok for lang tid' : err.message || 'Kunne ikke laste kategorier');
    
    // Auto-retry logic
    if (retryCount < 3) {
      setTimeout(() => {
        setRetryCount(prev => prev + 1);
        fetchCategories(true);
      }, 2000 * (retryCount + 1));
    }
  } finally {
    setCategoriesLoading(false);
  }
  }, [retryCount, formData.age]);


const handleManualRetry = useCallback(() => {
  setRetryCount(0);
  fetchCategories();
}, [fetchCategories]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

const fetchSubcategories = useCallback(async (categoryId, isRetry = false) => {
  if (subcategories[categoryId] && !isRetry) return;
  
  try {
    setSubcategoriesLoading(prev => ({ ...prev, [categoryId]: true }));
    if (!isRetry) {
      setSubcategoriesError(prev => ({ ...prev, [categoryId]: '' }));
    }
    
    // Lag en manual timeout controller
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 8000); // 8 sekunder
    
    // Include age parameter in the API call
    const response = await fetch(`${API_BASE_URL}/getSubcategories?categoryId=${categoryId}&ageGroup=${formData.age}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      signal: controller.signal
    });
    
    clearTimeout(timeoutId);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    
    if (data.success && data.subcategories) {
      setSubcategories(prev => ({ ...prev, [categoryId]: data.subcategories }));
      setSubcategoriesError(prev => ({ ...prev, [categoryId]: '' }));
      setSubcategoriesRetryCount(prev => ({ ...prev, [categoryId]: 0 }));
    } else {
      throw new Error(data.message || 'Ingen underkategorier mottatt fra server');
    }
  } catch (err) {
    console.error('Feil ved lasting av underkategorier:', err);
    setSubcategoriesError(prev => ({ 
      ...prev, 
      [categoryId]: err.name === 'AbortError' ? 'Forespørsel tok for lang tid' : err.message || 'Kunne ikke laste underkategorier' 
    }));
    
    // Auto-retry logic
    const currentRetryCount = subcategoriesRetryCount[categoryId] || 0;
    if (currentRetryCount < 2) {
      setTimeout(() => {
        setSubcategoriesRetryCount(prev => ({ 
          ...prev, 
          [categoryId]: currentRetryCount + 1 
        }));
        fetchSubcategories(categoryId, true);
      }, 1500 * (currentRetryCount + 1));
    }
  } finally {
    setSubcategoriesLoading(prev => ({ ...prev, [categoryId]: false }));
  }
}, [subcategories, subcategoriesRetryCount, formData.age]);


  const handleSubcategoriesRetry = useCallback((categoryId) => {
    setSubcategoriesRetryCount(prev => ({ ...prev, [categoryId]: 0 }));
    fetchSubcategories(categoryId);
  }, [fetchSubcategories]);

const findSimilarProducts = async (product, categoryName) => {
  const key = `${product.title}-${categoryName}`;

  // Om vi allerede har data, toggler vi bare utvidelse
  if (similarProducts[key]) {
    setExpandedProducts(prev => ({ ...prev, [key]: !prev[key] }));
    return;
  }

  try {
    // Sett loading for akkurat dette produktet
    setLoadingSimilar(prev => ({ ...prev, [key]: true }));
    const response = await fetch(`${API_BASE_URL}/findSimilarProducts`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ productName: product.title, budget: formData.budget, originalDescription: product.description, category: categoryName })
    });
    const data = await response.json();
    if (data.success) {
      setSimilarProducts(prev => ({ ...prev, [key]: data.products }));
      setExpandedProducts(prev => ({ ...prev, [key]: true }));
    }
  } catch (err) {
    setError('Kunne ikke finne lignende produkter');
  } finally {
    // Fjern loading-flagget
    setLoadingSimilar(prev => ({ ...prev, [key]: false }));
  }
};


const generateGifts = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Track search submission med analytics utility
      analytics.giftFinder.searched(formData);
      
      const response = await fetch(`${API_BASE_URL}/generateGift`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (!response.ok) throw new Error('Kunne ikke generere gaveforslag');
      
      const data = await response.json();
      
      // Track results
      if (data.categories) {
        const totalProducts = data.categories.reduce((sum, cat) => sum + (cat.products?.length || 0), 0);
        analytics.giftFinder.resultsShown(totalProducts);
      }
      
      setGiftResults(data.categories);
      setStep(4);
      
      setTimeout(() => {
        document.getElementById('results-section')?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } catch (err) {
      setError('Feil ved generering av gaver: ' + err.message);
    } finally {
      setLoading(false);
      setCompactFormExpanded(false);
    }
  };


  // Event handlers
  const handleCategoryToggle = async (categoryId) => {
    const isSelected = formData.interests.includes(categoryId);
    const newInterests = isSelected 
      ? formData.interests.filter(id => id !== categoryId)
      : [...formData.interests, categoryId];

    const newDetails = { ...formData.interestDetails };
    if (isSelected) {
      delete newDetails[categoryId];
    } else {
      await fetchSubcategories(categoryId);
      newDetails[categoryId] = [];
    }

    setFormData(prev => ({
      ...prev,
      interests: newInterests,
      interestDetails: newDetails
    }));
  };



const handleSubcategoryToggle = useCallback((categoryId, subcategoryId) => {
    // Preserve scroll position before state update
    const scrollY = window.scrollY;
    
    const current = formData.interestDetails[categoryId] || [];
    const updated = current.includes(subcategoryId)
      ? current.filter(id => id !== subcategoryId)
      : [...current, subcategoryId];

    setFormData(prev => ({
      ...prev,
      interestDetails: { ...prev.interestDetails, [categoryId]: updated }
    }));

    // Restore scroll position after React has had time to re-render
    requestAnimationFrame(() => {
      window.scrollTo(0, scrollY);
    });
  }, [formData.interestDetails]);

  const reset = () => {
    setStep(1);
    setFormData({
      age: 25, gender: '', budget: '200-500', interests: [], interestDetails: {},
      otherInterests: '', relationship: '', occasion: '', additional: '', specificRequest: ''
    });
    setGiftResults(null);
    setError('');
    setSimilarProducts({});
    setExpandedProducts({});
    setExpandedCategories({});
  };

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Components
  const Slider = ({ label, icon: Icon, value, onChange, min, max, options = [], showValue = false }) => (
  <div>
    <label className="block text-sm font-medium text-secondary-700 mb-4">
      <Icon className="w-4 h-4 inline mr-2" />
      {label}
    </label>

    <CustomSlider
      value={value}
      min={min}
      max={max}
      // Når showValue=true bruker vi ingen forhånds­definerte merker,
      // så slideren automatisk viser den løpende numeriske verdien.
      marks={showValue ? [] : options}
      onChange={onChange}
      className="mt-2"
    />

    {/* Faste merker under slideren */}
    {options.length > 0 && !showValue && (
      <div className="flex justify-between text-xs text-secondary-500 mt-1">
        {options.map((opt, idx) => (
          <span key={idx} className={idx === 0 || idx === options.length - 1 ? '' : 'hidden sm:block'}>
            {opt}
          </span>
        ))}
      </div>
    )}

    {/* Løpende verdi under slideren når showValue=true */}
    {showValue && (
      <div className="text-xs text-secondary-500 mt-1">
        {value} {typeof value === 'number' ? 'år' : ''}
      </div>
    )}
  </div>
);

  // The only part that needs to be modified is the CategoryGrid component
// The CompactSearchForm already filters selected categories from the dropdown

const CategoryGrid = ({ onToggle, selected = [] }) => {
  if (categoriesLoading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {[...Array(8)].map((_, index) => (
          <div key={index} className="p-4 rounded-lg border-2 border-secondary-200 ">
            <div className="w-8 h-8 bg-secondary-200 rounded mx-auto mb-2"></div>
            <div className="h-4 bg-secondary-200 rounded"></div>
          </div>
        ))}
      </div>
    );
  }

  if (categoriesError) {
    return (
      <div className="text-center py-8">
        <div className="bg-error-50 border border-error-200 rounded-lg p-6">
          <div className="text-error-600 mb-4">
            <p className="font-medium">Kunne ikke laste kategorier</p>
            <p className="text-sm mt-1">{categoriesError}</p>
            {retryCount > 0 && (
              <p className="text-xs mt-2">Forsøk {retryCount} av 3...</p>
            )}
          </div>
          <button
            onClick={handleManualRetry}
            disabled={categoriesLoading}
            className="px-4 py-2 bg-error-500 text-white rounded hover:bg-error-600 disabled:bg-error-300 transition-colors flex items-center space-x-2 mx-auto"
          >
            {categoriesLoading ? (
              <>
                <Loader className="w-4 h-4 animate-spin" />
                <span>Prøver igjen...</span>
              </>
            ) : (
              <span>Prøv igjen</span>
            )}
          </button>
        </div>
      </div>
    );
  };

  if (categories.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-secondary-500">Ingen kategorier tilgjengelig for øyeblikket.</p>
        <button
          onClick={handleManualRetry}
          className="mt-2 text-primary-600 hover:text-primary-800 underline"
        >
          Last inn på nytt
        </button>
      </div>
    );
  }

  // Filter out already selected categories
  const availableCategories = categories.filter(cat => !selected.includes(cat.id));
  
  // If all categories are selected, show a message
  if (availableCategories.length === 0 && selected.length > 0) {
    return (
      <div className="text-center p-4 bg-primary-50 border border-primary-200 rounded-lg">
        <p className="text-primary-600">Alle kategorier er valgt! Fjern en kategori for å velge en annen.</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
      {availableCategories.map(category => (
        <button
          key={category.id}
          onClick={() => onToggle(category.id)}
          className="p-4 rounded-lg border-2 border-secondary-200 hover:border-primary-300 text-secondary-700 transition-colors text-center"
        >
          <div className="text-2xl mb-2">{category.icon || '🎁'}</div>
          <div className="text-sm font-medium">{category.name}</div>
        </button>
      ))}
    </div>
  );
};

 const SubcategorySection = () => {
  if (formData.interests.length === 0) return null;

  return (
    <div>
      
      <label className="block text-sm font-medium text-secondary-700 mb-4">
        <Sparkles className="w-4 h-4 inline mr-2" />
        Oppgi detaljerte interesser (valgfritt)
      </label>
      <div className="space-y-4 mb-6">
        {formData.interests.map(categoryId => {
          const category = categories.find(cat => cat.id === categoryId);
          const subs = subcategories[categoryId];
          const isLoading = subcategoriesLoading[categoryId];
          const error = subcategoriesError[categoryId];
          const retryCount = subcategoriesRetryCount[categoryId] || 0;
          const selectedSubs = formData.interestDetails[categoryId] || [];
          
          return (
            <div key={categoryId} className="bg-secondary-50 rounded-lg border border-secondary-200">
              <button
                onClick={() => subs && subs.length > 0 && setExpandedCategories(prev => ({ 
                  ...prev, [categoryId]: !prev[categoryId] 
                }))}
                className="w-full flex items-center justify-between p-4 text-left hover:bg-secondary-100 transition-colors"
                disabled={!subs || subs.length === 0}
              >
                <div className="flex items-center space-x-2">
                  <span className="text-xl">{category?.icon || '🎁'}</span>
                  <span className="font-medium text-secondary-800">{category?.name}</span>
                  {selectedSubs.length > 0 && (
                    <span className="text-xs text-primary-600 bg-primary-100 px-1 rounded">
                      {selectedSubs.length}
                    </span>
                  )}
                  {isLoading && (
                    <Loader className="w-4 h-4 animate-spin ml-2 text-secondary-500" />
                  )}
                </div>
                
                <div className="flex items-center space-x-2">
                  {/* Show + or - depending on expanded state */}
                  {subs && subs.length > 0 && (
                    <span className="text-primary-600">
                      {expandedCategories[categoryId] ? '−' : '+'}
                    </span>
                  )}
                  
                  {/* × button to remove the category completely */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCategoryToggle(categoryId);
                    }}
                    className="text-secondary-600 hover:text-secondary-800 ml-2"
                    aria-label="Fjern kategori"
                  >
                    ×
                  </button>
                </div>
              </button>
              
              {isLoading && !subs && (
                <div className="px-4 pb-4">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {[...Array(6)].map((_, index) => (
                      <div key={index} className="flex items-center space-x-2 animate-pulse">
                        <div className="w-3 h-3 bg-secondary-300 rounded"></div>
                        <div className="h-3 bg-secondary-300 rounded flex-1"></div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {error && !subs && (
                <div className="text-center py-4">
                  <div className="text-error-600 text-sm mb-2">
                    <p>Kunne ikke laste underkategorier</p>
                    <p className="text-xs mt-1">{error}</p>
                    {retryCount > 0 && (
                      <p className="text-xs mt-1">Forsøk {retryCount} av 2...</p>
                    )}
                  </div>
                  <button
                    onClick={() => handleSubcategoriesRetry(categoryId)}
                    disabled={isLoading}
                    className="text-xs text-error-600 hover:text-error-800 underline disabled:opacity-50"
                  >
                    {isLoading ? 'Prøver igjen...' : 'Prøv igjen'}
                  </button>
                </div>
              )}
              
              {expandedCategories[categoryId] && subs?.length > 0 && (
                <div className="px-4 pb-4">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {subs.map(sub => (
                      <label key={sub.id} className="flex items-center space-x-2 text-sm cursor-pointer hover:bg-white p-2 rounded transition-colors">
                        <input
                          type="checkbox"
                          checked={selectedSubs.includes(sub.id)}
                          onChange={() => handleSubcategoryToggle(categoryId, sub.id)}
                          className="text-primary-500 focus:ring-primary-500 rounded"
                        />
                        <span className="text-secondary-700">{sub.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
  const ProductCard = ({ product, categoryName }) => {
  const key = `${product.title}-${categoryName}`;
  const isExpanded = expandedProducts[key];
  const similar = similarProducts[key];
  const isLoading = loadingSimilar[key];
  
  return (
    <div className="border border-secondary-200 rounded-lg overflow-hidden">
      <div className="p-4">
        <h4 className="font-semibold text-secondary-800 mb-2">{product.title}</h4>
        <p className="text-secondary-600 text-sm mb-3">{product.description}</p>
        {product.price && <p className="text-success-600 font-medium mb-3">{product.price}</p>}
        
        <div className="flex flex-wrap gap-2 items-center">
          {product.link && (
            <button
              onClick={() => handleExternalClick(product.title, 'google_search', product.link)}
              className="flex items-center space-x-1 text-accent-600 hover:text-accent-800 text-sm"
            >
              <Search className="w-4 h-4" />
              <span>Google</span>
              <ExternalLink className="w-3 h-3" />
            </button>
          )}
          {product.prisjaktLink && (
            <button
              onClick={() => handleExternalClick(product.title, 'prisjakt', product.prisjaktLink)}
              className="flex items-center space-x-1 text-orange-600 hover:text-orange-800 text-sm"
            >
              <ShoppingCart className="w-4 h-4" />
              <span>Prisjakt</span>
              <ExternalLink className="w-3 h-3" />
            </button>
          )}
          
                <button
          onClick={() => findSimilarProducts(product, categoryName)}
          className="flex items-center space-x-1 text-purple-600 hover:text-purple-800 text-sm ml-auto"
          disabled={isLoading}
        >
          {isLoading
            ? <>
                <Loader className="w-4 h-4 animate-spin" />
                <span>Finner lignende</span>
              </>
            : <>
                <Plus className="w-4 h-4" />
                <span>
                  {similar 
                    ? (isExpanded ? 'Skjul lignende' : 'Vis lignende') 
                    : 'Finn lignende'}
                </span>
              </>
          }
        </button>
        </div>
      </div>
        
        {isExpanded && similar && (
          <div className="border-t bg-secondary-50 p-4">
            <h5 className="font-medium text-secondary-700 mb-3">Lignende produkter:</h5>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {similar.map((item, idx) => (
                <div key={idx} className="bg-white border border-secondary-200 rounded p-3">
                  <h6 className="font-medium text-secondary-800 text-sm mb-1">{item.title}</h6>
                  <p className="text-secondary-600 text-xs mb-2">{item.description}</p>
                  {item.price && <p className="text-success-600 text-sm font-medium mb-2">{item.price}</p>}
                  <div className="flex space-x-2">
                    {item.link && (
                      <a href={item.link} target="_blank" rel="noopener noreferrer"
                         className="flex items-center space-x-1 text-accent-600 hover:text-accent-800 text-xs">
                        <Search className="w-3 h-3" />
                        <span>Google</span>
                      </a>
                    )}
                    {item.prisjaktLink && (
                      <a href={item.prisjaktLink} target="_blank" rel="noopener noreferrer"
                         className="flex items-center space-x-1 text-orange-600 hover:text-orange-800 text-xs">
                        <ShoppingCart className="w-3 h-3" />
                        <span>Prisjakt</span>
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };


const CompactSearchForm = () => (
  <div className="bg-white border-b shadow-lg sticky top-16 z-40">
    <div className="container mx-auto px-4 py-2 md:py-4">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-2 md:mb-4">
          {/* Mobile: Hele området klikkbart, Desktop: Bare tekst */}
          <button 
            onClick={() => setCompactFormExpanded(!compactFormExpanded)}
            className="flex items-center space-x-2 hover:text-primary-600 transition-colors"
            aria-label={compactFormExpanded ? "Skjul instillinger" : "Vis instillinger"}
          >
            <h2 className="text-base md:text-lg font-semibold text-secondary-800">Juster søket</h2>
            {/* Expand/collapse indicator for både mobile og desktop */}
            <span className="text-secondary-500">
              {compactFormExpanded ? '−' : '+'}
            </span>
          </button>
          <button onClick={reset} className="text-sm text-secondary-500 hover:text-secondary-700 underline">
            Nytt søk
          </button>
        </div>
          
          {/* Always show on desktop, toggleable on mobile */}
              <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 ${
                compactFormExpanded ? 'block md:grid' : 'hidden'
              }`}>
            {/* Alder */}
            <div>
              <label className="block text-xs font-medium text-secondary-600 mb-8">
                Alder: <span className="font-bold text-primary-600">{formData.age} år</span>
              </label>
              <CustomSlider
                value={formData.age}
                min={0}
                max={99}
                onChange={(e) => updateFormData('age', parseInt(e.target.value))}
                className="slider-compact"
              />
            </div>

            {/* Budsjett */}
            <div>
              <label className="block text-xs font-medium text-secondary-600 mb-8">
                Budsjett: <span className="font-bold text-primary-600">{budgetOptions.find(b => b.value === formData.budget)?.label}</span>
              </label>
              <CustomSlider
                value={budgetIndex}
                min={0}
                max={budgetMarks.length - 1}
                marks={budgetMarks}
                onChange={e => {
                  const idx = parseInt(e.target.value, 10);
                  updateFormData('budget', budgetOptions[idx].value);
                }}
                className="slider-compact"
              />
            </div>
            {/* Kategorier */}
            <div>
              <label className="block text-xs font-medium text-secondary-600 mb-2">
                Kategorier {formData.interests.length > 0 && 
                  <span className="font-bold text-primary-600">({formData.interests.length})</span>
                }
                {categoriesLoading && (
                  <span className="ml-2 text-xs text-secondary-500">(laster...)</span>
                )}
              </label>
              
              {categoriesLoading ? (
                <div className="w-full h-8 bg-secondary-200 rounded animate-pulse"></div>
              ) : categoriesError ? (
                <div className="w-full">
                  <div className="text-xs text-error-600 mb-1">{categoriesError}</div>
                  <button
                    onClick={handleManualRetry}
                    className="text-xs text-error-600 hover:text-error-800 underline"
                  >
                    Prøv igjen
                  </button>
                </div>
              ) : formData.interests.length === 0 ? (
                <select
                  onChange={(e) => e.target.value && handleCategoryToggle(e.target.value)}
                  className="w-full text-xs px-2 py-1 border border-secondary-300 rounded focus:ring-1 focus:ring-primary-500"
                  value=""
                >
                  <option value="">Velg kategori...</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              ) : (
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {formData.interests.map(categoryId => {
                    const category = categories.find(c => c.id === categoryId);
                    const subs = subcategories[categoryId] || [];
                    const selectedSubs = formData.interestDetails[categoryId] || [];
                    const isExpanded = expandedCategories[categoryId];
                    
                    return (
                      
                      <div key={categoryId} className="border border-secondary-200 rounded">
                        <div className="bg-primary-50">
                          {/* Hele området klikkbart for expand/collapse, unntatt × knappen */}
                          <button
                            onClick={() => subs.length > 0 && setExpandedCategories(prev => ({ 
                              ...prev, [categoryId]: !prev[categoryId] 
                            }))}
                            className={`w-full flex items-center justify-between px-2 py-1 text-left ${
                              subs.length > 0 ? 'hover:bg-primary-100' : ''
                            }`}
                            disabled={subs.length === 0}
                          >
                            <div className="flex items-center space-x-1">
                              <span className="text-primary-700 font-medium text-xs">
                                {category?.icon} {category?.name}
                              </span>
                              {selectedSubs.length > 0 && (
                                <span className="text-xs text-primary-600 bg-primary-100 px-1 rounded">
                                  {selectedSubs.length}
                                </span>
                              )}
                              {subs.length > 0 && (
                                <span className="text-primary-600 text-xs ml-auto">
                                  {isExpanded ? '−' : '+'}
                                </span>
                              )}
                            </div>
                            
                            {/* × knapp som stopper event propagation */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCategoryToggle(categoryId);
                              }}
                              className="text-primary-600 hover:text-primary-800 ml-2 flex-shrink-0"
                            >
                              ×
                            </button>
                          </button>
                        </div>
                        {isExpanded && subcategoriesLoading[categoryId] && !subs && (
                          <div className="p-2 bg-secondary-50 border-t">
                            <div className="grid grid-cols-2 gap-1">
                              {[...Array(4)].map((_, index) => (
                                <div key={index} className="flex items-center space-x-1 animate-pulse p-1">
                                  <div className="w-3 h-3 bg-secondary-300 rounded"></div>
                                  <div className="h-2 bg-secondary-300 rounded flex-1"></div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {isExpanded && subcategoriesError[categoryId] && !subs && (
                          <div className="p-2 bg-secondary-50 border-t text-center">
                            <div className="text-xs text-error-600 mb-1">Kunne ikke laste</div>
                            <button
                              onClick={() => handleSubcategoriesRetry(categoryId)}
                              className="text-xs text-error-600 hover:text-error-800 underline"
                            >
                              Prøv igjen
                            </button>
                          </div>
                        )}
                        
                        {isExpanded && subs.length > 0 && (
                          <div className="p-2 bg-secondary-50 border-t">
                            <div className="grid grid-cols-2 gap-1">
                              {subs.map(sub => (
                                <label key={sub.id} className="flex items-center space-x-1 text-xs cursor-pointer hover:bg-white p-1 rounded">
                                  <input
                                    type="checkbox"
                                    checked={selectedSubs.includes(sub.id)}
                                    onChange={() => handleSubcategoryToggle(categoryId, sub.id)}
                                    className="text-primary-500 focus:ring-primary-500 rounded w-3 h-3"
                                  />
                                  <span className="text-secondary-700 text-xs leading-tight">{sub.name}</span>
                                </label>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  
                  <select
                    onChange={(e) => {
                      if (e.target.value && !formData.interests.includes(e.target.value)) {
                        handleCategoryToggle(e.target.value);
                      }
                      e.target.value = "";
                    }}
                    className="w-full text-xs px-2 py-1 border border-secondary-300 rounded focus:ring-1 focus:ring-primary-500"
                    value=""
                  >
                    <option value="">+ Legg til kategori</option>
                    {categories.filter(cat => !formData.interests.includes(cat.id)).map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {/* Søk knapp */}
            <div className="flex items-end">
              <button
                onClick={generateGifts}
                disabled={loading}
                className="w-full px-4 py-2 bg-primary-500 text-white text-xs rounded hover:bg-primary-600 disabled:bg-secondary-300 transition-colors flex items-center justify-center space-x-1"
              >
                {loading ? (
                  <>
                    <Loader className="w-3 h-3 animate-spin" />
                    <span>Søker...</span>
                  </>
                ) : (
                  <>
                    <Search className="w-3 h-3" />
                    <span>Oppdater</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const LoadingMessage = () => (
    <div className="bg-gradient-to-r from-primary-50 to-purple-50 border border-primary-200 rounded-lg p-6 text-center">
      <div className="text-4xl mb-4">🎁</div>
      <h3 className="text-lg font-semibold text-secondary-800 mb-2">Gavedetektiven er på jobb! 🕵️‍♀️</h3>
      <p className="text-secondary-600 mb-2">Vi kikker gjennom <span className="font-semibold text-primary-600">tusenvis</span> av produkter...</p>
      <p className="text-sm text-secondary-500">⏰ Dette kan ta opptil 30 sekunder - perfekte gaver tar tid å finne! ✨</p>
    </div>
  );

  // Render logic
  if (step === 1) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-purple-50">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-6xl mx-auto">
            {(error || categoriesError) && (
              <div className="mb-6 p-4 bg-error-50 border border-error-200 rounded-lg">
                {error && <p className="text-error-600">{error}</p>}
                {categoriesError && (
                  <div className="text-error-600">
                    <p>Kategorier: {categoriesError}</p>
                    {retryCount > 0 && (
                      <p className="text-sm mt-1">Forsøk {retryCount} av 3 - prøver automatisk igjen...</p>
                    )}
                    <button 
                      onClick={handleManualRetry} 
                      className="text-error-500 hover:text-error-700 text-sm underline ml-2"
                    >
                      Prøv igjen nå
                    </button>
                  </div>
                )}
                <button 
                  onClick={() => {
                    setError('');
                    setCategoriesError('');
                  }} 
                  className="text-error-500 hover:text-error-700 text-sm underline ml-2"
                >
                  Lukk
                </button>
              </div>
            )}

            <div className="bg-white rounded-lg shadow-lg p-8">
              <div className="space-y-8">
                <div className="text-center">
                  <Gift className="w-16 h-16 text-primary-500 mx-auto mb-4" />
                  <h1 className="text-3xl font-bold text-secondary-800 mb-2">Gavefinner</h1>
                  <p className="text-secondary-600">La oss finne den perfekte gaven sammen!</p>
                </div>

                <div className="grid grid-cols-1 gap-8">
                  <Slider
                  label="Alder *"
                  icon={User}
                  value={formData.age}
                  onChange={e => {
                    const newAge = parseInt(e.target.value, 10);
                    updateFormData('age', newAge);
                    fetchCategories(); // Reload categories with new age
                  }}
                  min={0}
                  max={99}
                  showValue={true}
                  options={[]}
                />

                  <Slider
                    label="Budsjett *"
                    icon={CreditCard}
                    value={budgetIndex}  // Tall 0-4 som representerer hvilket nivå
                    onChange={e => {
                      const idx = parseInt(e.target.value, 10);
                      updateFormData('budget', budgetOptions[idx].value);
                    }}
                    min={0}
                    max={budgetMarks.length - 1}  // 0 til 4
                    options={budgetMarks}  // Bruker samme array som CompactSearchForm
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-4">
                    <Sparkles className="w-4 h-4 inline mr-2" />
                    Velg interesser (valgfritt)
                  </label>
                  <CategoryGrid onToggle={handleCategoryToggle} selected={formData.interests} />
                </div>

                <SubcategorySection />

                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button
                    onClick={generateGifts}
                    disabled={loading}
                    className="flex-1 sm:flex-none px-8 py-4 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:bg-secondary-300 transition-colors flex items-center justify-center space-x-2 text-lg font-medium"
                  >
                    {loading ? (
                      <>
                        <Loader className="w-5 h-5 animate-spin" />
                        <span>Søker...</span>
                      </>
                    ) : (
                      <>
                        <Gift className="w-5 h-5" />
                        <span>Søk etter gaver</span>
                      </>
                    )}
                  </button>
                  
                  <button
                    onClick={() => setStep(2)}
                    disabled={loading}
                    className="flex-1 sm:flex-none px-8 py-4 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:bg-secondary-300 transition-colors flex items-center justify-center space-x-2 text-lg font-medium"
                  >
                    <Settings className="w-5 h-5" />
                    <span>Avansert søk</span>
                  </button>
                </div>

                <div className="text-center text-sm text-secondary-500">
                  <p>💡 Velg "Søk etter gaver" for rask søk, eller "Avansert søk" for mer detaljerte forslag</p>
                </div>

                {loading && <LoadingMessage />}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (step === 2) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-purple-50">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-6xl mx-auto">
            <div className="mb-8">
              <div className="flex justify-between mb-2">
                <span className="text-sm text-secondary-600">Avansert søk</span>
                <span className="text-sm text-secondary-600">Steg 2 av 2</span>
              </div>
              <div className="w-full bg-secondary-200 rounded-full h-2">
                <div className="bg-primary-500 h-2 rounded-full w-full"></div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-lg p-8">
              <div className="space-y-6">
                <div className="text-center">
                  <Settings className="w-12 h-12 text-primary-500 mx-auto mb-4" />
                  <h2 className="text-2xl font-bold text-secondary-800 mb-2">Avansert søk</h2>
                  <p className="text-secondary-600">Legg til flere detaljer for enda bedre gaveforslag</p>
                </div>

                <div className="bg-secondary-50 rounded-lg p-4">
                  <h3 className="font-medium text-secondary-800 mb-2">Valgt informasjon:</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div><span className="text-secondary-600">Alder:</span> <span className="ml-2 font-medium">{formData.age} år</span></div>
                    <div><span className="text-secondary-600">Budsjett:</span> <span className="ml-2 font-medium">{budgetOptions.find(b => b.value === formData.budget)?.label}</span></div>
                    {formData.interests.length > 0 && (
                      <div className="md:col-span-2">
                        <span className="text-secondary-600">Kategori:</span>
                        <span className="ml-2 font-medium">{formData.interests.map(id => categories.find(c => c.id === id)?.name).join(', ')}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <select
                    value={formData.gender}
                    onChange={(e) => updateFormData('gender', e.target.value)}
                    className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">Velg kjønn (valgfritt)</option>
                    <option value="mann">Mann</option>
                    <option value="kvinne">Kvinne</option>
                    <option value="annet">Annet</option>
                  </select>

                  <select
                    value={formData.relationship}
                    onChange={(e) => updateFormData('relationship', e.target.value)}
                    className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">Velg relasjon (valgfritt)</option>
                    {options.relationships.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>

                  <select
                    value={formData.occasion}
                    onChange={(e) => updateFormData('occasion', e.target.value)}
                    className="md:col-span-2 w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">Velg anledning (valgfritt)</option>
                    {options.occasions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>

                <textarea
                  value={formData.otherInterests}
                  onChange={(e) => updateFormData('otherInterests', e.target.value)}
                  placeholder="Andre interesser (f.eks: kaffe, reising, fotografering...)"
                  className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  rows="2"
                />

                <textarea
                  value={formData.additional}
                  onChange={(e) => updateFormData('additional', e.target.value)}
                  placeholder="Ekstra informasjon om personen (f.eks: liker å være utendørs, samler på noe spesielt...)"
                  className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  rows="2"
                />

                <textarea
                  value={formData.specificRequest}
                  onChange={(e) => updateFormData('specificRequest', e.target.value)}
                  placeholder="Har du noe spesifikt i tankene? Beskriv det her..."
                  className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  rows="2"
                />

                <div className="flex justify-between">
                  <button
                    onClick={() => setStep(1)}
                    className="px-6 py-3 bg-secondary-500 text-white rounded-lg hover:bg-secondary-600 transition-colors"
                  >
                    ← Tilbake
                  </button>
                  <button
                    onClick={generateGifts}
                    disabled={loading}
                    className="px-6 py-3 bg-success-500 text-white rounded-lg hover:bg-success-600 disabled:bg-secondary-300 transition-colors flex items-center space-x-2"
                  >
                    {loading ? (
                      <>
                        <Loader className="w-4 h-4 animate-spin" />
                        <span>Genererer...</span>
                      </>
                    ) : (
                      <>
                        <Gift className="w-4 h-4" />
                        <span>Finn gaver!</span>
                      </>
                    )}
                  </button>
                </div>

                {loading && <LoadingMessage />}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (step === 4) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-purple-50">
        <CompactSearchForm />
        
        <div className="container mx-auto px-4 py-8" id="results-section">
          <div className="max-w-6xl mx-auto">
            <div className="space-y-8">
              <div className="text-center">
                <Gift className="w-12 h-12 text-success-500 mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-secondary-800 mb-2">Dine gaveforslag</h2>
                <p className="text-secondary-600">Her er personlige forslag basert på dine innspill</p>
              </div>

              {giftResults?.map((category, index) => (
                <div key={index} className="bg-white rounded-lg shadow-lg p-6">
                  <h3 className="text-xl font-bold text-secondary-800 mb-2">{category.name}</h3>
                  {category.description && <p className="text-secondary-600 mb-4">{category.description}</p>}
                  
                  <div className="space-y-4">
                    {category.products?.map((product, productIndex) => (
                      <ProductCard key={productIndex} product={product} categoryName={category.name} />
                    ))}
                  </div>
                </div>
              ))}

              <div className="text-center">
                <button
                  onClick={reset}
                  className="px-6 py-3 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
                >
                  Søk etter nye gaver
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

// CSS Styles (now much simpler since we use custom slider)
const styles = `
  .slider-main, .slider-compact {
    width: 100%;
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = styles;
  document.head.appendChild(styleSheet);
}

export default GiftFinderApp;
